import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { ActionItemStatus, assignOdometerReadingFormSchema } from '@ekhodealer/ekho-common';
import { Button, SkeletonRect,Stack, Text, TextInput, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useAssignOdometerReading } from '../../../api/progress/useAssignOdometerReading';
import { useGetAssignOdometerReading } from '../../../api/progress/useGetAssignOdometerReading';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface AssignOdometerReadingDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	odometerReading: number;
}

/**
 * AssignOdometerReadingDetail form component for assigning an odometer reading to a purchased vehicle.
 * Displays a text input for odometer reading entry with validation and a submit button.
 */
export const AssignOdometerReadingDetail = ({ actionItem }: AssignOdometerReadingDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const assignOdometerReadingMutation = useAssignOdometerReading(orderId);
	const { data: existingOdometerData, isLoading: isLoadingOdometerData } =
		useGetAssignOdometerReading(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(assignOdometerReadingFormSchema),
		defaultValues: {
			odometerReading: 0,
		},
	});

	// Pre-populate form with existing odometer reading data
	useEffect(() => {
		if (existingOdometerData?.odometerReading !== undefined) {
			reset({
				odometerReading: existingOdometerData.odometerReading,
			});
		}
	}, [existingOdometerData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		try {
			await assignOdometerReadingMutation.mutateAsync({
				odometerReading: data.odometerReading,
			});
			toast({
				message: 'Odometer reading assigned successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to assign odometer reading';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Enter the odometer reading (in miles) to assign to this order. The reading must be a non-negative whole number (0 or greater).'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{isLoadingOdometerData ? (
					<SkeletonRect width='100%' height={48} />
				) : (
					<Controller
						name='odometerReading'
						control={control}
						render={({ field: { onChange, onBlur, value } }) => (
							<Stack itemsSpacing='4'>
								<Text size='footnote'>Odometer Reading (miles)</Text>
								<TextInput
									placeholder='Enter odometer reading'
									value={value ? value.toString() : ''}
									onChange={(val) => {
										// Only allow digits
										const digitsOnly = val.replace(/\D/g, '');
										onChange(digitsOnly ? Number.parseInt(digitsOnly, 10) : 0);
									}}
									onBlur={onBlur}
									size='large'
									hasError={errors.odometerReading?.message}
									isDisabled={
										isSubmitting ||
										assignOdometerReadingMutation.isPending ||
										actionItem.status === ActionItemStatus.COMPLETE
									}
									type='number'
								/>
								{errors.odometerReading && (
									<Text size='footnote' color='error'>
										{errors.odometerReading.message}
									</Text>
								)}
							</Stack>
						)}
					/>
				)}

				{actionItem.status !== ActionItemStatus.COMPLETE && (
					<Button
						size='large'
						isFullWidth
						onPress={() => {
							submitHandler();
						}}
						isLoading={isSubmitting || assignOdometerReadingMutation.isPending}
						isDisabled={isLoadingOdometerData}>
						{actionItem.config.buttonTitle || 'Assign odometer reading'}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
